import {
  CLINIC_CRF_V1,
  CLINIC_CRF_V2,
  CONDITION_OPERATORS,
  FIELD_SOURCES,
  FIELD_TYPES,
  MISSING_CODES,
  type ClinicCrfCondition,
  type ClinicCrfDefinition,
  type ClinicCrfDefinitionV1,
  type ClinicCrfDefinitionV2,
  type ClinicCrfField,
  type ValidationIssue,
  type ValidationResult,
} from './types'
import { deepClone, isRecord, unicodeLength } from './runtime'

const PATH_PATTERN = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/
const FORBIDDEN_PATH_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor'])
const ID_PATTERN = /^[a-z][a-z0-9_.-]*$/
const V1_TYPES = FIELD_TYPES.filter((type) => type !== 'partial_date')
const BASE_FIELD_KEYS = [
  'path',
  'label',
  'type',
  'required',
  'unit',
  'min',
  'max',
  'precision',
  'options',
  'terminology',
  'source',
  'min_length',
  'max_length',
] as const
const V2_FIELD_KEYS = [
  ...BASE_FIELD_KEYS,
  'missing_codes',
  'missing_reason_required',
  'visible_when',
  'required_when',
] as const

function issue(
  issues: ValidationIssue[],
  path: string,
  message: string,
  code: string,
  severity: 'error' | 'warning' = 'error',
): void {
  issues.push({ path, message, code, severity })
}

function unknownKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
  issues: ValidationIssue[],
): void {
  Object.keys(value)
    .filter((key) => !allowed.includes(key))
    .forEach((key) => issue(issues, path ? `${path}.${key}` : key, 'Nieobsługiwany element kontraktu.', 'unknown_key'))
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function containsUnsafeNumber(value: unknown): boolean {
  if (typeof value === 'number') return !Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER
  if (Array.isArray(value)) return value.some(containsUnsafeNumber)
  return isRecord(value) && Object.values(value).some(containsUnsafeNumber)
}

function validateCondition(
  value: unknown,
  paths: Set<string>,
  path: string,
  issues: ValidationIssue[],
  depth = 0,
  state = { nodes: 0 },
): value is ClinicCrfCondition {
  state.nodes += 1
  if (depth > 8 || state.nodes > 100) {
    issue(issues, path, 'Warunek przekracza bezpieczny limit złożoności.', 'condition_complexity')
    return false
  }
  if (!isRecord(value)) {
    issue(issues, path, 'Warunek musi być obiektem.', 'condition_object')
    return false
  }
  const composite = ['all', 'any', 'not'].filter((key) => key in value)
  if (composite.length) {
    if (composite.length !== 1 || Object.keys(value).length !== 1) {
      issue(issues, path, 'Warunek złożony musi zawierać dokładnie jeden operator all, any albo not.', 'condition_composite')
      return false
    }
    const operator = composite[0] as 'all' | 'any' | 'not'
    const children = value[operator]
    if (operator === 'not') {
      return validateCondition(children, paths, `${path}.not`, issues, depth + 1, state)
    }
    if (!Array.isArray(children) || children.length === 0) {
      issue(issues, `${path}.${operator}`, `Operator ${operator} wymaga niepustej listy warunków.`, 'condition_children')
      return false
    }
    let valid = true
    children.forEach((child, index) => {
      if (!validateCondition(child, paths, `${path}.${operator}.${index}`, issues, depth + 1, state)) valid = false
    })
    return valid
  }

  unknownKeys(value, ['field', 'operator', 'value', 'compare_field'], path, issues)
  let valid = true
  if (typeof value.field !== 'string' || !paths.has(value.field)) {
    issue(issues, `${path}.field`, 'Warunek musi wskazywać pole z tej wersji formularza.', 'condition_field')
    valid = false
  }
  if (typeof value.operator !== 'string' || !CONDITION_OPERATORS.includes(value.operator as never)) {
    issue(issues, `${path}.operator`, 'Nieobsługiwany operator warunku.', 'condition_operator')
    return false
  }
  const hasValue = Object.prototype.hasOwnProperty.call(value, 'value')
  const hasCompareField = Object.prototype.hasOwnProperty.call(value, 'compare_field')
  if (['present', 'missing'].includes(value.operator)) {
    if (hasValue || hasCompareField) {
      issue(issues, path, `Operator ${value.operator} nie przyjmuje wartości porównawczej.`, 'condition_operand')
      valid = false
    }
    return valid
  }
  if (hasValue === hasCompareField) {
    issue(issues, path, 'Warunek wymaga dokładnie jednej wartości albo pola porównawczego.', 'condition_operand')
    valid = false
  }
  if (hasCompareField && (typeof value.compare_field !== 'string' || !paths.has(value.compare_field))) {
    issue(issues, `${path}.compare_field`, 'Pole porównawcze nie istnieje w tej wersji formularza.', 'condition_compare_field')
    valid = false
  }
  if (['in', 'not_in'].includes(value.operator)) {
    if (!hasValue || !Array.isArray(value.value) || value.value.length === 0) {
      issue(issues, `${path}.value`, `Operator ${value.operator} wymaga niepustej listy wartości.`, 'condition_list')
      valid = false
    } else if (value.value.some((entry) => !['string', 'number', 'boolean'].includes(typeof entry) && entry !== null)) {
      issue(issues, `${path}.value`, 'Lista porównawcza może zawierać wyłącznie wartości skalarne.', 'condition_scalar')
      valid = false
    }
  } else if (hasValue && (Array.isArray(value.value) || (typeof value.value === 'object' && value.value !== null))) {
    issue(issues, `${path}.value`, 'Ten operator wymaga wartości skalarnej.', 'condition_scalar')
    valid = false
  }
  if (hasValue && containsUnsafeNumber(value.value)) {
    issue(issues, `${path}.value`, 'Liczba przekracza bezpieczny zakres interoperacyjny JSON.', 'condition_number_range')
    valid = false
  }
  return valid
}

function validateField(
  value: unknown,
  index: number,
  v2: boolean,
  issues: ValidationIssue[],
): value is ClinicCrfField {
  const path = `fields.${index}`
  if (!isRecord(value)) {
    issue(issues, path, 'Pole musi być obiektem.', 'field_object')
    return false
  }
  unknownKeys(value, v2 ? V2_FIELD_KEYS : BASE_FIELD_KEYS, path, issues)
  let valid = true
  if (typeof value.path !== 'string' || !PATH_PATTERN.test(value.path) || value.path.length > 120
    || value.path.split('.').some((segment) => FORBIDDEN_PATH_SEGMENTS.has(segment))) {
    issue(issues, `${path}.path`, 'Ścieżka musi mieć postać lower_snake_case z opcjonalnymi segmentami oddzielonymi kropką.', 'field_path')
    valid = false
  }
  if (typeof value.label !== 'string' || !value.label.trim() || unicodeLength(value.label) > 500) {
    issue(issues, `${path}.label`, 'Etykieta jest wymagana i może mieć maksymalnie 500 znaków.', 'field_label')
    valid = false
  }
  const allowedTypes = v2 ? FIELD_TYPES : V1_TYPES
  if (typeof value.type !== 'string' || !allowedTypes.includes(value.type as never)) {
    issue(issues, `${path}.type`, 'Nieobsługiwany typ pola.', 'field_type')
    valid = false
  }
  if (value.required !== undefined && typeof value.required !== 'boolean') {
    issue(issues, `${path}.required`, 'Wymagana jest wartość logiczna.', 'field_required')
    valid = false
  }
  if (value.unit !== undefined && value.unit !== null && (typeof value.unit !== 'string' || unicodeLength(value.unit) > 40)) {
    issue(issues, `${path}.unit`, 'Jednostka może mieć maksymalnie 40 znaków.', 'field_unit')
    valid = false
  }
  for (const key of ['min', 'max'] as const) {
    if (value[key] !== undefined && !finiteNumber(value[key])) {
      issue(issues, `${path}.${key}`, 'Wymagana jest skończona liczba.', `field_${key}`)
      valid = false
    }
    if (finiteNumber(value[key]) && Math.abs(value[key]) > Number.MAX_SAFE_INTEGER) {
      issue(issues, `${path}.${key}`, 'Liczba przekracza bezpieczny zakres interoperacyjny JSON.', `field_${key}_range`)
      valid = false
    }
  }
  if (finiteNumber(value.min) && finiteNumber(value.max) && value.min > value.max) {
    issue(issues, `${path}.max`, 'Maksimum nie może być mniejsze od minimum.', 'field_range')
    valid = false
  }
  if (value.precision !== undefined && (!Number.isInteger(value.precision) || Number(value.precision) < 0 || Number(value.precision) > 10)) {
    issue(issues, `${path}.precision`, 'Precyzja musi być liczbą całkowitą 0–10.', 'field_precision')
    valid = false
  }
  for (const key of ['min_length', 'max_length'] as const) {
    if (value[key] !== undefined && (!Number.isInteger(value[key]) || Number(value[key]) < (key === 'min_length' ? 0 : 1) || Number(value[key]) > 100000)) {
      issue(issues, `${path}.${key}`, 'Nieprawidłowe ograniczenie długości.', `field_${key}`)
      valid = false
    }
  }
  if (Number.isInteger(value.min_length) && Number.isInteger(value.max_length) && Number(value.min_length) > Number(value.max_length)) {
    issue(issues, `${path}.max_length`, 'Maksymalna długość nie może być mniejsza od minimalnej.', 'field_length_range')
    valid = false
  }
  if (value.source !== undefined && (typeof value.source !== 'string' || !FIELD_SOURCES.includes(value.source as never))) {
    issue(issues, `${path}.source`, 'Nieobsługiwane źródło danych.', 'field_source')
    valid = false
  }
  if (value.terminology !== undefined && !isRecord(value.terminology) && !Array.isArray(value.terminology)) {
    issue(issues, `${path}.terminology`, 'Terminologia musi być obiektem albo listą metadanych.', 'field_terminology')
    valid = false
  }
  if (value.terminology !== undefined && containsUnsafeNumber(value.terminology)) {
    issue(issues, `${path}.terminology`, 'Terminologia zawiera liczbę poza bezpiecznym zakresem interoperacyjnym JSON.', 'field_terminology_number_range')
    valid = false
  }
  if (value.options !== undefined) {
    if (!Array.isArray(value.options) || value.options.length === 0 || value.options.some((entry) => typeof entry !== 'string' || !entry.trim() || unicodeLength(entry) > 200)) {
      issue(issues, `${path}.options`, 'Opcje muszą być niepustą listą tekstów do 200 znaków.', 'field_options')
      valid = false
    } else if (new Set(value.options).size !== value.options.length) {
      issue(issues, `${path}.options`, 'Opcje nie mogą się powtarzać.', 'field_options_unique')
      valid = false
    }
  }
  if (['choice', 'multichoice'].includes(String(value.type)) && (!Array.isArray(value.options) || value.options.length === 0)) {
    issue(issues, `${path}.options`, 'Pole wyboru wymaga listy opcji.', 'field_options_required')
    valid = false
  }
  if (v2 && value.missing_codes !== undefined) {
    if (!Array.isArray(value.missing_codes) || value.missing_codes.length === 0 || value.missing_codes.some((code) => !MISSING_CODES.includes(code as never))) {
      issue(issues, `${path}.missing_codes`, 'Lista zawiera nieobsługiwany kod braku danych.', 'field_missing_codes')
      valid = false
    } else if (new Set(value.missing_codes).size !== value.missing_codes.length) {
      issue(issues, `${path}.missing_codes`, 'Kody braku danych nie mogą się powtarzać.', 'field_missing_codes_unique')
      valid = false
    }
  }
  if (v2 && value.missing_reason_required !== undefined && typeof value.missing_reason_required !== 'boolean') {
    issue(issues, `${path}.missing_reason_required`, 'Wymagana jest wartość logiczna.', 'field_missing_reason')
    valid = false
  }
  if (v2 && value.missing_reason_required === true && (!Array.isArray(value.missing_codes) || value.missing_codes.length === 0)) {
    issue(issues, `${path}.missing_codes`, 'Wymaganie powodu wymaga co najmniej jednego kodu braku danych.', 'field_missing_reason_codes')
    valid = false
  }
  if (value.type === 'partial_date' && ['min', 'max', 'precision', 'options', 'min_length', 'max_length'].some((key) => key in value)) {
    issue(issues, `${path}.type`, 'Data częściowa nie obsługuje ograniczeń liczbowych, tekstowych ani listy opcji.', 'field_partial_constraints')
    valid = false
  }
  return valid
}

export function validateDefinition(input: unknown): ValidationResult<ClinicCrfDefinition> {
  const issues: ValidationIssue[] = []
  if (!isRecord(input)) {
    issue(issues, '', 'Definicja CRF musi być obiektem JSON.', 'definition_object')
    return { value: null, issues }
  }
  const v2 = input.version === CLINIC_CRF_V2
  const v1 = input.version === CLINIC_CRF_V1
  if (!v1 && !v2) {
    issue(issues, 'version', 'Obsługiwane są clinic-crf.v1 i clinic-crf.v2.', 'definition_version')
    return { value: null, issues }
  }
  unknownKeys(input, v2 ? ['version', 'fields', 'rules'] : ['version', 'fields'], '', issues)
  if (!Array.isArray(input.fields) || input.fields.length < 1 || input.fields.length > 500) {
    issue(issues, 'fields', 'Definicja wymaga od 1 do 500 pól.', 'definition_fields')
    return { value: null, issues }
  }

  input.fields.forEach((field, index) => validateField(field, index, v2, issues))
  const paths = input.fields
    .filter(isRecord)
    .map((field) => field.path)
    .filter((path): path is string => typeof path === 'string')
  const pathSet = new Set<string>()
  paths.forEach((path, index) => {
    if (pathSet.has(path)) issue(issues, `fields.${index}.path`, 'Ścieżka występuje więcej niż raz.', 'field_path_unique')
    pathSet.add(path)
  })
  paths.forEach((path, index) => {
    if (paths.some((other, otherIndex) => otherIndex !== index && other.startsWith(`${path}.`))) {
      issue(issues, `fields.${index}.path`, 'Ścieżka koliduje z polem zagnieżdżonym.', 'field_path_collision')
    }
  })

  if (v2) {
    input.fields.forEach((field, index) => {
      if (!isRecord(field)) return
      for (const key of ['visible_when', 'required_when'] as const) {
        if (field[key] !== undefined) validateCondition(field[key], pathSet, `fields.${index}.${key}`, issues)
      }
    })
    if (input.rules !== undefined && (!Array.isArray(input.rules) || input.rules.length > 500)) {
      issue(issues, 'rules', 'Reguły muszą być listą maksymalnie 500 elementów.', 'rules_array')
    } else if (Array.isArray(input.rules)) {
      const ids = new Set<string>()
      input.rules.forEach((rule, index) => {
        const path = `rules.${index}`
        if (!isRecord(rule)) {
          issue(issues, path, 'Reguła musi być obiektem.', 'rule_object')
          return
        }
        unknownKeys(rule, ['id', 'path', 'message', 'assert', 'when'], path, issues)
        if (typeof rule.id !== 'string' || !ID_PATTERN.test(rule.id) || rule.id.length > 120) {
          issue(issues, `${path}.id`, 'Id reguły ma nieprawidłowy format.', 'rule_id')
        } else if (ids.has(rule.id)) issue(issues, `${path}.id`, 'Id reguły występuje więcej niż raz.', 'rule_id_unique')
        else ids.add(rule.id)
        if (typeof rule.path !== 'string' || !pathSet.has(rule.path)) issue(issues, `${path}.path`, 'Reguła musi wskazywać istniejące pole.', 'rule_path')
        if (typeof rule.message !== 'string' || !rule.message.trim() || unicodeLength(rule.message) > 1000) issue(issues, `${path}.message`, 'Komunikat reguły jest wymagany i może mieć maksymalnie 1000 znaków.', 'rule_message')
        validateCondition(rule.assert, pathSet, `${path}.assert`, issues)
        if (rule.when !== undefined) validateCondition(rule.when, pathSet, `${path}.when`, issues)
      })
    }
  }

  const errors = issues.filter((candidate) => candidate.severity === 'error')
  if (errors.length) return { value: null, issues }
  return { value: deepClone(input) as unknown as ClinicCrfDefinition, issues }
}

export function migrateV1ToV2(definition: ClinicCrfDefinitionV1): ClinicCrfDefinitionV2 {
  return {
    version: CLINIC_CRF_V2,
    fields: deepClone(definition.fields) as ClinicCrfField[],
    rules: [],
  }
}

export function lintDefinition(definition: ClinicCrfDefinitionV2): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  definition.fields.forEach((field, index) => {
    const path = `fields.${index}`
    if (!field.source) issue(issues, `${path}.source`, 'Warto jawnie wskazać źródło danych.', 'lint_source', 'warning')
    if (['string', 'text', 'boolean', 'date', 'datetime'].includes(field.type) && field.options) {
      issue(issues, `${path}.options`, 'Lista opcji nie ma znaczenia dla tego typu pola.', 'lint_unused_options', 'warning')
    }
    if (field.type === 'integer' && field.precision !== undefined) {
      issue(issues, `${path}.precision`, 'Precyzja nie ma znaczenia dla liczby całkowitej.', 'lint_integer_precision', 'warning')
    }
    if (field.required && field.required_when) {
      issue(issues, `${path}.required_when`, 'Pole jest już zawsze wymagane; warunek required_when nie zmienia zachowania.', 'lint_required_condition', 'warning')
    }
    if (field.source === 'derived' && field.required) {
      issue(issues, `${path}.required`, 'Pole wyliczane nie powinno wymagać ręcznego wprowadzenia.', 'lint_derived_required', 'warning')
    }
  })
  ;(definition.rules ?? []).forEach((rule, index) => {
    if (!rule.when) issue(issues, `rules.${index}.when`, 'Reguła bez when jest oceniana również przy pustych danych.', 'lint_rule_always', 'warning')
  })
  return issues
}

export function validateDefinitionV2(input: unknown): ValidationResult<ClinicCrfDefinitionV2> {
  const result = validateDefinition(input)
  if (!result.value) return { value: null, issues: result.issues }
  if (result.value.version === CLINIC_CRF_V1) {
    return {
      value: migrateV1ToV2(result.value),
      issues: [
        ...result.issues,
        {
          path: 'version',
          message: 'Definicja clinic-crf.v1 została jawnie zmigrowana do v2.',
          code: 'migrated_v1',
          severity: 'warning',
        },
      ],
      migratedFrom: CLINIC_CRF_V1,
    }
  }
  return { value: result.value, issues: [...result.issues, ...lintDefinition(result.value)] }
}
